import { inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subscription } from 'rxjs';

// UI
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { UiCommandEvent } from '@ui/cer-ui-types';

// Cerium
import { CerSessionService } from '@cer/cer-session/cer-session.service';

// Form
import { CerFormComponent } from '@form/cer-form.component';
import { CerFormService } from '@form/cer-form.service';
import { CerFormView } from '@form/cer-form-view';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormDataNavigation, CerFormNavigationService } from '@form/cer-form-navigation.service';
import { CerFormPaneType } from '@form/cer-form-panes/cer-form-panes.service';
import { CerFormContext } from '@form/cer-form-context';
import { CerFormFactoryViewSet } from '@form/cer-form-factory/cer-form-factory-view-set';
import { CerFormPaneViewFactory } from './cer-form-panes/cer-form-pane-view-factory';
import { CerFormPaneView } from './cer-form-panes/cer-form-pane/cer-form-pane-view';

// App
import { CerAppRouteService } from 'src/cer-app/cer-app-route/cer-app-route.service';
import { CerAppStateService } from 'src/cer-app/cer-app-state/cer-app-state.service';


@Injectable()
export class CerFormContextService implements OnDestroy {

  // State
  private viewSets: CerFormViewSet[] = [];

  // View context 
  public form: CerFormComponent;

  public initFromForm(form: CerFormComponent) {
    this.form = form;
    this.routeService.setIsLoading(false);
    this.viewSetShow();
  }

  protected onPaneTabsCommand(pane: CerFormPaneType, e: UiCommandEvent) {
    this.form.commmand.emit(e);
  }

  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  public ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  public navigationService = inject(CerFormNavigationService);
  private formService = inject(CerFormService);
  public routeService = inject(CerAppRouteService);
  private ui = inject(CerPromptService);
  private stateService = inject(CerAppStateService);
  private sessionService = inject(CerSessionService);
  private injector = inject(Injector);

  // Public 
  public contextClone(parentView: CerFormView = null): CerFormContext {
    var ctx = new CerFormContext();
    ctx.form = this.form;
    ctx.formService = this.formService;
    ctx.routeService = this.routeService;
    ctx.ui = this.ui;
    ctx.stateService = this.stateService;
    ctx.session = this.sessionService;
    ctx.injector = this.injector;
    ctx.subscriptionManager = this.subscriptionManager;
    ctx.parentView = parentView;
    return ctx;
  }

  public activate(view: CerFormView) {
    if (view == null) {
      return;
    }
    var paneView: CerFormPaneView;
    this.viewSets.forEach(vs => vs.paneViews.forEach(pv => {
      if (pv.view == view) { paneView = pv }
    }));
    new CerFormPaneViewFactory(this.form, this.viewSets).paneViewSelect(paneView);
  }

  public navigate(nav: CerFormDataNavigation) {
    if (this.form?.paneComponents?.length > 0) {
      // Form has panes to support CerFormView 
      this.navigationShow(nav);
    }
    else if (nav.url?.length > 0) {
      // Fall back (no panes availabe)
      window.open(this.sessionService.environment.baseUrl + nav.url, "_blank");
    }
  }

  public entityShow(entityNameChild: string, columnNameChild: string = null, parentView: CerFormView, target: CerFormPaneType = null, columnNameParent: string = null, filterValue: any = null, callerData: any): void {
    const nav = this.navigationService.navigationByEntityColumnGet(entityNameChild, columnNameChild, columnNameParent, filterValue, parentView);
    if (nav == null) {
      return;
    }
    if (target) {
      nav.paneTarget = target;
    }
    this.navigationShow(nav, callerData);
  }

  public navigationShow(nav: CerFormDataNavigation, callerData: any = null) {
    CerFormFactoryViewSet.viewSetBuildByNavigation(this.contextClone(nav.parentView), nav, callerData).then(
      viewSet => {
        this.viewSetShowOrMakeVisible(viewSet, nav.mandatory !== true);
      }
    );
  }

  public activatedRouteShow(activatedRoute: ActivatedRoute) {
    CerFormFactoryViewSet.viewSetBuildByActivatedRoute(this.contextClone(), activatedRoute).then(
      viewSet => {
        if (viewSet != null) {
          if (viewSet.title?.length > 0) {
            this.routeService.setRouteTitle(viewSet.title);
          }
          this.form.panes ??= viewSet.panes;
          this.viewSets = []; // ActivatedRoute is allways an initial navigation
          this.viewSetShow(viewSet);
        }
      });
  }

  public viewSetShowOrMakeVisible(viewSetToShow: CerFormViewSet, disableMandatoryViews: boolean = false ) {
    if (disableMandatoryViews) {
      viewSetToShow.paneViews?.forEach(pv => pv.mandatory = false); // Secondary view sets are never mandatory
    }

    var viewSetFound: CerFormViewSet = this.viewSets?.find(s => CerFormFactoryViewSet.routeOptionsEqual(s.routeOptions, viewSetToShow.routeOptions));
    if (viewSetFound != null) {
      // ViewSet is already added
      // Ensure existing views are visible + update data link
      this.viewSetShow(viewSetFound, viewSetToShow);
    }
    else {
      this.viewSetShow(viewSetToShow);
    }
  }

  public viewSetShow(viewSet: CerFormViewSet = null, viewSetNewBuild: CerFormViewSet = null) {
    if (viewSet != null && viewSetNewBuild == null) {
      this.viewSets.push(viewSet);
    }
    if (this.form && this.viewSets?.length > 0) {
      new CerFormPaneViewFactory(this.form, this.viewSets).viewSetShow(viewSet, viewSetNewBuild);
    }
  }

}
